import type { EntityReferenceId, ShoppingTrip, ShoppingTripItem } from "../../app/types.js";

// Optimistic cart state for the in-store check-off (#702).
//
// A row toggle used to await a POST plus a full shopping refetch under a global
// busy lock. It now patches the trip locally and reconciles with whatever the
// request hands back. Keeping that arithmetic here — pure, trip-in/trip-out —
// means the interesting cases (rollback, two taps in flight, a realtime refresh
// landing mid-flight) are unit-testable without a DOM.

// Mirrors the server's line math: priceMinor is per item, lines are price x qty.
export function lineTotalMinor(item: Pick<ShoppingTripItem, "priceMinor" | "quantity">): number {
  return Math.round(item.priceMinor * item.quantity);
}

// Re-derives the money the same way the API does, so an optimistic row moves the
// cart total without waiting for a round-trip.
export function recomputeTripTotals(trip: ShoppingTrip): ShoppingTrip {
  const subtotalMinor = trip.items.reduce((total, item) => total + lineTotalMinor(item), 0);
  const taxableSubtotalMinor = trip.items.reduce((total, item) => total + (item.taxable ? lineTotalMinor(item) : 0), 0);
  const rateMillis = Math.round(trip.salesTaxRatePercent * 1000);
  const taxMinor = Math.round((taxableSubtotalMinor * rateMillis) / 100000);
  return {
    ...trip,
    itemCount: trip.items.length,
    taxableItemCount: trip.items.filter((item) => item.taxable).length,
    subtotalMinor,
    taxMinor,
    totalMinor: subtotalMinor + taxMinor
  };
}

// The API returns cart items oldest-first; local inserts keep that order so an
// optimistic row doesn't jump when the real one replaces it.
export function sortCartItems(items: ShoppingTripItem[]): ShoppingTripItem[] {
  return [...items].sort((left, right) => (
    left.addedAt === right.addedAt
      ? String(left.id).localeCompare(String(right.id))
      : left.addedAt.localeCompare(right.addedAt)
  ));
}

// Client-only ids for rows that exist only while their POST is in flight.
// Distinct from the offline outbox's "pending-" ids, which are queued mutations
// a later load replays against the server.
const optimisticIdPrefix = "optimistic-";
let optimisticSequence = 0;

export function nextOptimisticId(): EntityReferenceId {
  optimisticSequence += 1;
  return `${optimisticIdPrefix}${optimisticSequence}` as EntityReferenceId;
}

export function isOptimisticTripItem(item: ShoppingTripItem): boolean {
  return String(item.id).startsWith(optimisticIdPrefix);
}

export function insertCartItem(trip: ShoppingTrip, item: ShoppingTripItem): ShoppingTrip {
  if (trip.items.some((entry) => entry.id === item.id)) return trip;
  return recomputeTripTotals({ ...trip, items: sortCartItems([...trip.items, item]) });
}

export function dropCartItem(trip: ShoppingTrip, itemId: EntityReferenceId): ShoppingTrip {
  const items = trip.items.filter((entry) => entry.id !== itemId);
  return items.length === trip.items.length ? trip : recomputeTripTotals({ ...trip, items });
}

// Swap a placeholder for the row the server (or the offline outbox) created.
// Tolerates the placeholder already being gone — a realtime refresh can replace
// the trip mid-flight — and never duplicates an id that is already present.
export function settleCartItem(trip: ShoppingTrip, placeholderId: EntityReferenceId, item: ShoppingTripItem): ShoppingTrip {
  const rest = trip.items.filter((entry) => entry.id !== placeholderId && entry.id !== item.id);
  return recomputeTripTotals({ ...trip, items: sortCartItems([...rest, item]) });
}

// Reconcile a bulk "check off remaining" response. `serverTrip` is the refreshed
// trip the bulk endpoint returns (null when the batch was queued offline, in
// which case `addedItems` are the local pending stubs). Rows belonging to some
// other tap still in flight survive either way.
//
// `preserveItemIds` covers the narrow race the server snapshot cannot: a single
// tap whose POST succeeded — so its row is real and no longer optimistic — but
// whose INSERT committed after the bulk handler read the trip back. Those ids
// come from a 200 response we saw ourselves, so keeping them can only restore a
// row the server holds; it can never resurrect one somebody else deleted.
export function mergeBulkCheckOff(
  trip: ShoppingTrip,
  options: {
    serverTrip: ShoppingTrip | null;
    addedItems: ShoppingTripItem[];
    placeholderIds: ReadonlySet<EntityReferenceId>;
    preserveItemIds?: ReadonlySet<EntityReferenceId>;
  }
): ShoppingTrip {
  const preserve = options.preserveItemIds ?? new Set<EntityReferenceId>();
  const carried = trip.items.filter((entry) => {
    // This batch's own placeholders always lose — they are what `settled` replaces.
    if (options.placeholderIds.has(entry.id)) return false;
    return isOptimisticTripItem(entry) || preserve.has(entry.id);
  });
  const settled = options.serverTrip
    ? options.serverTrip.items
    : [...trip.items.filter((entry) => !isOptimisticTripItem(entry)), ...options.addedItems];
  const settledIds = new Set(settled.map((entry) => entry.id));
  return recomputeTripTotals({
    ...(options.serverTrip ?? trip),
    items: sortCartItems([...settled, ...carried.filter((entry) => !settledIds.has(entry.id))])
  });
}
